/** Temporary expand/contract adapter. A transaction's title describes what it was for;
 * its Payee identifies the person or business. Price observations use payeeId/payeeName for their shop. */
export function normalizeTitle<T extends object>(value: T): T {
  const record = value as Record<string, unknown>;
  if (!("vendor" in record)) return value;
  const title = record.title === undefined ? record.vendor : record.title;
  return Object.fromEntries(Object.entries(record)
    .filter(([field]) => field !== "vendor" || record.title === undefined)
    .map(([field, item]) => field === "vendor" ? ["title", title] : [field, field === "title" ? title : item])) as T;
}

/** A legacy price name is an explicitly supplied Payee name, never a transaction title. */
export function normalizePricePayee<T extends object>(value: T): T {
  const record = value as Record<string, unknown>;
  if (!("vendor" in record)) return value;
  const { vendor, ...fields } = record;
  return { ...fields, payeeName: record.payeeName === undefined ? vendor : record.payeeName } as T;
}

/** Read old persisted drafts, caches, receipts and Moo proposals without changing their identities.
 * Validation remains the responsibility of the receiving service. Explicit title values win. */
export function normalizeLedgerFields<T>(value: T): T {
  return mapFields(value, false) as T;
}

/** Only the HTTP compatibility boundary exposes legacy transaction/price aliases to old clients. */
export function legacyLedgerAliases<T>(value: T, legacyOnly = false): T {
  return mapFields(value, true, "", legacyOnly) as T;
}

function mapFields(value: unknown, legacy: boolean, key = "", legacyOnly = false): unknown {
  if (Array.isArray(value)) return value.map(item => mapFields(item, legacy, key, legacyOnly));
  if (!value || typeof value !== "object") return value;
  const record = value as Record<string, unknown>;
  const entry = ["expense", "expected", "patch"].includes(key) ||
    ("accountId" in record && "amount" in record);
  const price = !entry && "total" in record && (["priorPrice", "restorePrices"].includes(key) || "observationId" in record ||
    ("date" in record && ("currency" in record || "transactionId" in record)));
  const source = entry ? normalizeTitle(record) : price ? normalizePricePayee(record) : record;
  const result: Record<string, unknown> = Object.fromEntries(Object.entries(source)
    .map(([field, item]) => [field, mapFields(item, legacy, field, legacyOnly)]));
  if (entry && legacy && "title" in result) result.vendor = result.title;
  if (entry && legacyOnly) delete result.title;
  if (price && legacy && "payeeName" in result) result.vendor = result.payeeName;
  if (price && legacyOnly) {
    delete result.payeeName;
    // Older Moo price editors identify a shop by name and cannot maintain a selected ID.
    if (key === "item") delete result.payeeId;
  }
  if ("tripVendors" in result) {
    result.tripTitles ??= result.tripVendors;
    if (!legacy) delete result.tripVendors;
  }
  if (legacy && "tripTitles" in result) result.tripVendors = result.tripTitles;
  if (legacyOnly && "tripTitles" in result) delete result.tripTitles;
  return result;
}

/** Old persisted editor snapshots may have a different property order after alias conversion. */
export function transactionTitleSnapshot(value: unknown): string {
  return JSON.stringify(normalizeLedgerFields(value), (_key, item: unknown) =>
    item && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b)))
      : item);
}
